import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, ChefHat, Pill, HeartPulse, ClipboardList, Salad, Dumbbell, Activity, FlaskConical, UserCircle } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
interface Session {
  name: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
}
const followUps: Session[] = [{
  name: '30-Minute Follow-Up',
  duration: 'Single Session',
  description: "A focused check-in to fine-tune your plan, troubleshoot what's working, and keep momentum on track."
}, {
  name: '30-Minute Follow-Up · 3 Pack',
  duration: '3 Sessions',
  description: 'Best for anyone working toward a 2-3 month goal who wants accountability and consistent course-correction.'
}, {
  name: '30-Minute Follow-Up · 5 Pack',
  duration: '5 Sessions',
  description: 'Highly recommended if you have 20+ pounds to lose or are navigating a longer GLP-1 protocol.',
  badge: 'Popular',
  highlight: true
}, {
  name: '30-Minute Follow-Up · 10 Pack',
  duration: '10 Sessions',
  description: 'For sustained transformation — keeps you focused, motivated, and progressing toward long-term goals.'
}];
const classes = [{
  icon: ChefHat,
  title: 'Cooking Classes',
  description: "Hands-on, virtual or in-person sessions focused on high-protein, GLP-1-friendly meals you'll actually want to eat. Learn techniques, batch-prep strategies, and flavor-building principles.",
  format: 'Group or Private',
  duration: '60–90 min'
}, {
  icon: Pill,
  title: 'Supplement Education',
  description: "A deep-dive class on building a smart supplement protocol. We'll cover the essentials, what's actually worth your money, and how to evaluate quality and third-party testing.",
  format: 'Group or 1:1',
  duration: '60 min'
}, {
  icon: HeartPulse,
  title: 'Diabetes Education',
  description: 'Comprehensive class on managing blood sugar through nutrition, lifestyle, and medication coordination. Ideal for prediabetes, type 2 diabetes, or anyone wanting to optimize metabolic health.',
  format: 'Group or 1:1',
  duration: '75 min'
}];
interface SessionCardProps {
  title: string;
  duration: string;
  description: string;
  badge?: string;
  highlight?: boolean;
  variant?: 'light' | 'dark';
}
function SessionCard({
  title,
  duration,
  description,
  badge,
  highlight,
  variant = 'light'
}: SessionCardProps) {
  const isDark = variant === 'dark';
  return <div className={`relative flex flex-col h-full p-8 border transition-all ${highlight ? 'bg-tealslate text-white border-tealslate' : isDark ? 'bg-navyslate/30 border-darkgray hover:border-tealslate/60 text-white' : 'bg-white border-[#D8D8D0] hover:border-tealslate/60 text-[#1F1F1F]'}`} data-id="element-1912">
      {badge && <span className={`absolute -top-3 left-8 text-[10px] font-bold uppercase tracking-widest px-3 py-1 ${highlight ? 'bg-burgundy text-white' : 'bg-tealslate text-white'}`} data-id="element-1913">
          {badge}
        </span>}

      <div className="flex items-center gap-2 mb-4" data-id="element-1914">
        <Clock className={`w-4 h-4 ${highlight ? 'text-white/70' : isDark ? 'text-tealslate' : 'text-tealslate'}`} data-id="element-1915" />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${highlight ? 'text-white/70' : isDark ? 'text-[#9CA3AF]' : 'text-[#7A7A70]'}`} data-id="element-1916">
          {duration}
        </span>
      </div>

      <h3 className={`text-2xl font-serif mb-4 leading-tight ${highlight ? 'text-white' : isDark ? 'text-white' : 'text-[#1F1F1F]'}`} data-id="element-1917">
        {title}
      </h3>

      <p className={`text-sm leading-relaxed mb-6 flex-1 ${highlight ? 'text-white/90' : isDark ? 'text-[#D8D8D0]' : 'text-[#3B3B3B]'}`} data-id="element-1918">
        {description}
      </p>

      <Link to="/contact" className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mt-auto self-start pb-1 border-b-2 transition-colors ${highlight ? 'text-white border-white hover:border-white/50' : 'text-tealslate border-tealslate hover:border-tealslate/50'}`} data-id="element-1919">
        Inquire to Book <ArrowRight className="w-3 h-3" data-id="element-1920" />
      </Link>
    </div>;
}
export function Services() {
  return <div className="w-full min-h-screen bg-[#F5F5F0]" data-id="element-1921">
      {/* HERO */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-24 px-6 md:px-10 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/vjWZMHEwqi3KaAveCCjapz/ChatGPT_Image_May_19,_2026,_11_42_45_PM.png')"
    }} data-id="element-1922">
        {/* Soft cream overlay on the left so the copy reads cleanly */}
        <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(to right, rgba(245,245,240,0.92) 0%, rgba(245,245,240,0.85) 40%, rgba(245,245,240,0.35) 70%, rgba(245,245,240,0.1) 100%)'
      }} data-id="element-1923" />

        <div className="relative max-w-7xl mx-auto" data-id="element-1924">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-xl" data-id="element-1925">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: '#5A6B3F'
          }} data-id="element-1926">
              Our Services
            </motion.p>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-[#1F1F1F] leading-[1.05] mb-2" data-id="element-1927">
              Personalized support.
            </motion.h1>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif italic leading-[1.05] mb-8" style={{
            color: '#3B4A4F'
          }} data-id="element-1928">
              Proven results.
            </motion.h1>
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#3A3A33] leading-relaxed max-w-lg mb-10" data-id="element-1929">
              At Structured Nutrition, our services are designed to meet you
              where you are and help you reach your health goals with
              science-backed strategies, expert guidance, and ongoing support.
            </motion.p>
            <motion.div variants={fadeUpVariants} data-id="element-1930">
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center justify-center gap-2 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-1931">
                Send a Message
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE OFFER — Services grid */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-1932">
        <div className="max-w-7xl mx-auto" data-id="element-1933">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-1934">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-4" style={{
            color: '#5A6B3F'
          }} data-id="element-1935">
              What We Offer
            </motion.p>
            <motion.h2 variants={fadeUpVariants} className="text-4xl md:text-5xl font-serif text-[#1F1F1F] leading-[1.1]" data-id="element-1936">
              Services tailored to you
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1937">
            {[{
            icon: ClipboardList,
            title: '1:1 Nutrition Coaching',
            description: 'Personalized nutrition coaching built around your goals, lifestyle, and preferences to help you achieve lasting results.'
          }, {
            icon: Salad,
            title: 'Personalized Meal Plans',
            description: 'Custom meal plans designed to fit your goals, food preferences, and lifestyle, making healthy eating simple and sustainable.'
          }, {
            icon: Dumbbell,
            title: 'Performance Nutrition',
            description: 'Nutrition strategies to fuel your workouts, enhance performance, and support recovery so you can perform at your best.'
          }, {
            icon: Activity,
            title: 'Strength Training Guidance',
            description: 'Expert training guidance to help you build muscle, increase strength, and improve body composition safely and effectively.'
          }, {
            icon: FlaskConical,
            title: 'Supplement Recommendations',
            description: 'Science-backed supplement recommendations tailored to your needs to help fill gaps and support your overall health.'
          }, {
            icon: UserCircle,
            title: 'Accountability Coaching',
            description: 'Ongoing support, check-ins, and accountability to keep you consistent, motivated, and on track with your goals.'
          }].map(({
            icon: Icon,
            title,
            description
          }) => <motion.div key={title} variants={fadeUpVariants} className="bg-[#F0EFE8] p-10 text-center flex flex-col items-center" data-id="element-1938">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 border" style={{
              backgroundColor: '#E4E6D6',
              borderColor: '#D2D5C0'
            }} data-id="element-1939">
                  <Icon className="w-7 h-7" style={{
                color: '#5A6B3F'
              }} strokeWidth={1.5} data-id="element-1940" />
                </div>
                <h3 className="font-serif text-xl text-[#1F1F1F] mb-4" data-id="element-1941">
                  {title}
                </h3>
                <p className="text-sm text-[#3A3A33] leading-relaxed mb-6 flex-1" data-id="element-1942">
                  {description}
                </p>
                <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="text-[11px] font-bold uppercase tracking-[0.25em] inline-flex items-center gap-2 transition-colors" style={{
              color: '#5A6B3F'
            }} data-id="element-1943">
                  Learn More <ArrowRight className="w-3 h-3" data-id="element-1944" />
                </Link>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="bg-[#FBFAF4] py-20 md:py-28 px-6" data-id="element-1945">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1946">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-1947">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-5" style={{
            color: '#5A6B3F'
          }} data-id="element-1948">
              Our Approach
            </motion.p>
            <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.1] mb-2" data-id="element-1949">
              Science-backed.
            </motion.h2>
            <motion.h2 variants={fadeUpVariants} className="font-serif italic text-4xl md:text-5xl leading-[1.1] mb-8" style={{
            color: '#5A6B3F'
          }} data-id="element-1950">
              Person-centered.
            </motion.h2>
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#3A3A33] leading-relaxed mb-10 max-w-2xl mx-auto" data-id="element-1951">
              We combine the latest nutrition science with a personalized
              approach to help you build sustainable habits, improve your
              health, and feel your best, without restrictive diets or
              one-size-fits-all solutions.
            </motion.p>

            <motion.ul variants={fadeUpVariants} className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-8 gap-y-4" data-id="element-1952">
              {['Evidence-based strategies', 'Personalized to your lifestyle', 'Sustainable, realistic, and effective'].map(item => <li key={item} className="flex items-center gap-3 text-[15px] text-[#1F1F1F]" data-id="element-1953">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full border shrink-0" style={{
                borderColor: '#5A6B3F'
              }} data-id="element-1954">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#5A6B3F" strokeWidth="2" className="w-4 h-4" data-id="element-1955">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" data-id="element-1956" />
                    </svg>
                  </span>
                  {item}
                </li>)}
            </motion.ul>
          </motion.div>
        </div>
      </section>

      {/* READY TO GET STARTED — final CTA strip */}
      <section className="bg-[#F5F5F0] pb-20 md:pb-28 px-6" data-id="element-1957">
        <div className="max-w-7xl mx-auto" data-id="element-1958">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={fadeUpVariants} className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-6 justify-between" style={{
          backgroundColor: '#ECEDE2',
          border: '1px solid #D2D5C0'
        }} data-id="element-1959">
            <div className="flex items-start md:items-center gap-5 md:gap-6" data-id="element-1960">
              <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{
              backgroundColor: '#3B4A4F'
            }} data-id="element-1961">
                <Clock className="w-6 h-6 text-white" strokeWidth={1.8} data-id="element-1962" />
              </div>
              <div data-id="element-1963">
                <h3 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-2 leading-tight" data-id="element-1964">
                  Ready to get started?
                </h3>
                <p className="text-sm md:text-base text-[#3A3A33] leading-relaxed max-w-lg" data-id="element-1965">
                  Send a message to learn more about our services and find the
                  right fit for you. We respond personally within 48 hours.
                </p>
              </div>
            </div>

            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center justify-center gap-2 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors shrink-0" style={{
            backgroundColor: '#3B4A4F'
          }} data-id="element-1966">
              Send a Message
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}